import { useEffect, useState } from 'react';
import { BRAND } from '../../config/brand';
import { CARD_MM, DISPLAY_FONT, SANS_FONT, fitSize, qrPath, textWidthMm } from '../../utils/nfcPrint';

/**
 * Print art for the black NFC cards, as SVG strings in millimetres
 * (viewBox 0 0 85.6 54). The same string drives the on-screen preview, the
 * SVG download and the PNG export, so what you see is what prints.
 * Nothing paints a background: the black card shows through.
 */

export const FRONT_VARIANTS = [
  { id: 'centered', name: 'Centered', description: 'Logo on top, name centered below' },
  { id: 'executive', name: 'Executive', description: 'Logo top left, name bottom left' },
  { id: 'split', name: 'Split', description: 'Logo left, details right, divider' },
  { id: 'emblem', name: 'Emblem', description: 'Big logo left, details right. Best for round or square logos' },
];

/** Layouts built around the profile photo (offered when the card has one). */
export const PHOTO_VARIANTS = [
  { id: 'portrait', name: 'Portrait', description: 'Round photo left, details right' },
  { id: 'profile', name: 'Profile', description: 'Round photo on top, name centered below' },
  { id: 'panel', name: 'Photo panel', description: 'Tall photo right, details left' },
  { id: 'duo', name: 'Photo + logo', description: 'Photo and logo side by side, name centered below' },
  { id: 'signature', name: 'Signature', description: 'Photo and details left, big logo right' },
];
export const isPhotoVariant = (id) => PHOTO_VARIANTS.some((v) => v.id === id);

export const QR_STYLES = [
  { id: 'tile', name: 'White tile', description: 'Dark code on a white tile. Scans most reliably.' },
  { id: 'modules', name: 'White code only', description: 'White code straight on the black card. Most phones read it, some older scanners don\'t.' },
];

const W = CARD_MM.width;
const H = CARD_MM.height;
const SOFT = '#C9D6E6'; // secondary text: light enough on black
const MUTED = '#9FB0C4';

const esc = (s) =>
  String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
const n = (v) => Number(v.toFixed(3));

function open(fontCss) {
  return `<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="${W}mm" height="${H}mm" viewBox="0 0 ${W} ${H}">`
    + `<defs><style>${fontCss || ''}</style></defs>`;
}

function text(str, { x, y, size, family = SANS_FONT, weight = 500, fill = '#FFFFFF', anchor = 'start', spacing = 0 }) {
  if (!str) return '';
  return `<text x="${n(x)}" y="${n(y)}" font-family="${esc(family)}" font-weight="${weight}" font-size="${n(size)}"`
    + (spacing ? ` letter-spacing="${n(spacing)}"` : '')
    + ` fill="${fill}" text-anchor="${anchor}">${esc(str)}</text>`;
}

/**
 * Fit an image of the given aspect into a box; align: 'center' | 'left'.
 * logo.plate: put the logo on a rounded white background (inside the same box).
 */
function image(logo, { x, y, w, h, align = 'center', valign = 'middle' }) {
  const aspect = logo.aspect || 1;
  const pad = logo.plate ? Math.max(1, Math.min(w, h) * 0.14) : 0;
  const iw = Math.min(w - 2 * pad, (h - 2 * pad) * aspect);
  const ih = iw / aspect;
  const bw = iw + 2 * pad;
  const bh = ih + 2 * pad;
  const bx = align === 'left' ? x : x + (w - bw) / 2;
  const by = valign === 'bottom' ? y + h - bh : valign === 'top' ? y : y + (h - bh) / 2;
  const plate = logo.plate
    ? `<rect x="${n(bx)}" y="${n(by)}" width="${n(bw)}" height="${n(bh)}" rx="${n(Math.min(1.6, bh / 4))}" fill="#FFFFFF"/>`
    : '';
  return `${plate}<image href="${logo.href}" xlink:href="${logo.href}" x="${n(bx + pad)}" y="${n(by + pad)}" width="${n(iw)}" height="${n(ih)}" preserveAspectRatio="xMidYMid meet"/>`;
}

function monogram(initials, { cx, cy, r, accent }) {
  const size = r * (initials.length > 1 ? 0.78 : 0.95);
  return `<circle cx="${n(cx)}" cy="${n(cy)}" r="${n(r)}" fill="none" stroke="${accent}" stroke-width="0.4"/>`
    + text(initials, { x: cx, y: cy + size * 0.36, size, family: DISPLAY_FONT, weight: 800, anchor: 'middle' });
}

/** Photo cropped to fill a circle, with a thin accent ring. */
function photoCircle(photo, { cx, cy, r, accent }) {
  return `<clipPath id="photo-clip"><circle cx="${n(cx)}" cy="${n(cy)}" r="${n(r)}"/></clipPath>`
    + `<image href="${photo.href}" xlink:href="${photo.href}" x="${n(cx - r)}" y="${n(cy - r)}" width="${n(2 * r)}" height="${n(2 * r)}" preserveAspectRatio="xMidYMid slice" clip-path="url(#photo-clip)"/>`
    + `<circle cx="${n(cx)}" cy="${n(cy)}" r="${n(r + 0.7)}" fill="none" stroke="${accent}" stroke-width="0.35"/>`;
}

/** Photo cropped to fill a rounded rectangle. */
function photoRect(photo, { x, y, w, h, r = 2 }) {
  return `<clipPath id="photo-clip"><rect x="${n(x)}" y="${n(y)}" width="${n(w)}" height="${n(h)}" rx="${n(r)}"/></clipPath>`
    + `<image href="${photo.href}" xlink:href="${photo.href}" x="${n(x)}" y="${n(y)}" width="${n(w)}" height="${n(h)}" preserveAspectRatio="xMidYMid slice" clip-path="url(#photo-clip)"/>`;
}

/** Round, square or tall logos (seals, badges) need a taller box than wide wordmarks to read at the same size. */
const compact = (logo) => (logo?.aspect || 1) < 1.6;

const nameStyle = (sizeMm) => ({ family: DISPLAY_FONT, weight: 800, sizeMm });
const titleStyle = (sizeMm) => ({ family: SANS_FONT, weight: 500, sizeMm });
const companyStyle = (sizeMm) => ({ family: SANS_FONT, weight: 600, sizeMm, letterSpacingMm: sizeMm * 0.16 });

/**
 * Name (one line, or two when long), title and company, left aligned and
 * vertically centered on cy.
 */
function leftBlock({ x, maxW, cy, name, title, companyText, accent, soft, nameMm = 4.6 }) {
  let out = '';
  let nameLines = [name];
  let ns = fitSize(name, nameStyle(nameMm), maxW, 2.4);
  const words = name.trim().split(/\s+/);
  if (ns < nameMm * 0.78 && words.length > 1) {
    const mid = Math.ceil(words.length / 2);
    nameLines = [words.slice(0, mid).join(' '), words.slice(mid).join(' ')];
    const longest = nameLines.reduce((a, b) => (textWidthMm(a, nameStyle(nameMm)) >= textWidthMm(b, nameStyle(nameMm)) ? a : b));
    ns = fitSize(longest, nameStyle(nameMm), maxW, 2.4);
  }
  const ts = fitSize(title, titleStyle(2.4), maxW, 1.6);
  const cs = fitSize(companyText, companyStyle(1.9), maxW, 1.4);
  const nameH = nameLines.length * ns * 1.08;
  const blockH = nameH + (title ? 2.2 + ts : 0) + (companyText ? 3.6 + cs : 0);
  let y = cy - blockH / 2;
  for (const line of nameLines) {
    y += ns * (line === nameLines[0] ? 0.92 : 1.08);
    out += text(line, { x, y, size: ns, family: DISPLAY_FONT, weight: 800 });
  }
  if (title) { y += 2.2 + ts * 0.92; out += text(title, { x, y, size: ts, fill: soft }); }
  if (companyText) { y += 3.6 + cs * 0.92; out += text(companyText, { x, y, size: cs, weight: 600, fill: accent, spacing: cs * 0.16 }); }
  return out;
}

/** Company, title, name and a short accent rule, stacked up from the bottom of the safe zone. */
function bottomStack({ x, maxW, name, title, companyText, accent, soft }) {
  let out = '';
  const ns = fitSize(name, nameStyle(5.4), maxW, 3);
  const ts = fitSize(title, titleStyle(2.6), maxW, 1.8);
  const cs = fitSize(companyText, companyStyle(2.0), maxW, 1.5);
  let y = 48.6;
  if (companyText) { out += text(companyText, { x, y, size: cs, weight: 600, fill: accent, spacing: cs * 0.16 }); y -= cs + 2.6; }
  if (title) { out += text(title, { x, y, size: ts, fill: soft }); y -= ts + 2.8; }
  out += text(name, { x, y, size: ns, family: DISPLAY_FONT, weight: 800 });
  out += `<rect x="${x}" y="${n(y - ns - 2.2)}" width="7" height="0.6" rx="0.3" fill="${accent}"/>`;
  return out;
}

/**
 * Front of the card.
 * @param {object} o { variant, name, title, company, initials, logo: {href, aspect, plate?}|null, photo: {href, aspect}|null, accent, fontCss }
 * Photo layouts (PHOTO_VARIANTS) need a photo; without a logo they simply leave it out.
 */
export function buildFront(o) {
  const { variant = 'centered', name = '', title = '', company = '', initials = '?', logo, photo, fontCss, whiteOnly = false } = o;
  // White ink only: one color for everything (accents and secondary text too)
  const accent = whiteOnly ? '#FFFFFF' : o.accent || '#FFFFFF';
  const soft = whiteOnly ? '#FFFFFF' : SOFT;
  const companyText = (company || '').toUpperCase();
  let body = '';

  if (photo && variant === 'portrait') {
    const r = 12;
    body += photoCircle(photo, { cx: 7 + r, cy: 27, r, accent });
    const x = 7 + 2 * r + 6;
    const maxW = W - 7 - x;
    const lh = compact(logo) ? 11 : 7.5;
    if (logo) body += image(logo, { x, y: 7, w: Math.min(maxW, 30), h: lh, align: 'left', valign: 'top' });
    body += leftBlock({ x, maxW, cy: logo ? 27 + (lh - 3.5) / 2 + 2 : 27, name, title, companyText, accent, soft });
  } else if (photo && variant === 'profile') {
    const cx = W / 2;
    const maxW = W - 16;
    body += photoCircle(photo, { cx, cy: 16.5, r: 9.5, accent });
    const ns = fitSize(name, nameStyle(5), maxW, 3);
    const ts = fitSize(title, titleStyle(2.5), maxW, 1.8);
    body += text(name, { x: cx, y: 34, size: ns, family: DISPLAY_FONT, weight: 800, anchor: 'middle' });
    if (title) body += text(title, { x: cx, y: 38.8, size: ts, fill: soft, anchor: 'middle' });
    if (logo) {
      // The logo stands in for the company name
      body += image(logo, { x: cx - 15, y: 41.4, w: 30, h: compact(logo) ? 9.4 : 6.6 });
    } else if (company) {
      const cs = fitSize(companyText, companyStyle(2.0), maxW, 1.5);
      body += `<rect x="${n(cx - 4)}" y="41.6" width="8" height="0.5" rx="0.25" fill="${accent}"/>`;
      body += text(companyText, { x: cx + (cs * 0.16) / 2, y: 46.4, size: cs, weight: 600, fill: accent, anchor: 'middle', spacing: cs * 0.16 });
    }
  } else if (photo && variant === 'panel') {
    const pw = 25;
    const px = W - 7 - pw;
    body += photoRect(photo, { x: px, y: 7, w: pw, h: H - 14 });
    const x = 7;
    const maxW = px - 4.5 - x;
    if (logo) body += image(logo, { x, y: 7, w: Math.min(maxW, 30), h: compact(logo) ? 14 : 10, align: 'left', valign: 'top' });
    body += bottomStack({ x, maxW, name, title, companyText, accent, soft });
  } else if (photo && variant === 'duo') {
    const cx = W / 2;
    const maxW = W - 16;
    const r = 10;
    if (logo) {
      const gap = 5;
      const lw = Math.min(30, 2 * r * Math.max(1, logo.aspect || 1));
      const total = 2 * r + gap + lw;
      const x0 = cx - total / 2;
      body += photoCircle(photo, { cx: x0 + r, cy: 7.7 + r, r, accent });
      body += `<rect x="${n(x0 + 2 * r + gap / 2 - 0.15)}" y="${n(7.7 + 3)}" width="0.3" height="${n(2 * r - 6)}" fill="${accent}"/>`;
      body += image(logo, { x: x0 + 2 * r + gap, y: 7.7, w: lw, h: 2 * r });
    } else {
      body += photoCircle(photo, { cx, cy: 7.7 + r, r, accent });
    }
    const ns = fitSize(name, nameStyle(5), maxW, 3);
    const ts = fitSize(title, titleStyle(2.5), maxW, 1.8);
    body += text(name, { x: cx, y: 37.2, size: ns, family: DISPLAY_FONT, weight: 800, anchor: 'middle' });
    if (title) body += text(title, { x: cx, y: 42.2, size: ts, fill: soft, anchor: 'middle' });
    if (company) {
      const cs = fitSize(companyText, companyStyle(1.9), maxW, 1.4);
      body += text(companyText, { x: cx + (cs * 0.16) / 2, y: 47.6, size: cs, weight: 600, fill: accent, anchor: 'middle', spacing: cs * 0.16 });
    }
  } else if (photo && variant === 'signature') {
    // Photo top left, details below it, the logo big on the right
    const ls = logo ? 30 : 0;
    const lx = W - 7 - ls;
    if (logo) body += image(logo, { x: lx, y: (H - ls) / 2, w: ls, h: ls });
    const r = 9;
    body += photoCircle(photo, { cx: 7.7 + r, cy: 7.7 + r, r, accent });
    body += leftBlock({ x: 7, maxW: (logo ? lx - 5 : W - 7) - 7, cy: 38.6, name, title, companyText, accent, soft, nameMm: 4.2 });
  } else if (variant === 'emblem') {
    const ls = 32;
    if (logo) body += image(logo, { x: 7, y: (H - ls) / 2, w: ls, h: ls });
    else body += monogram(initials, { cx: 7 + ls / 2, cy: 27, r: ls / 2 - 2, accent });
    const x = 7 + ls + 5.5;
    body += `<rect x="${n(x - 2.9)}" y="15" width="0.3" height="24" fill="${accent}"/>`;
    body += leftBlock({ x, maxW: W - 7 - x, cy: 27, name, title, companyText, accent, soft, nameMm: 4.2 });
  } else if (variant === 'executive') {
    const x = 7;
    const maxW = W - 2 * x;
    if (logo) body += image(logo, { x, y: 7, w: 36, h: compact(logo) ? 16 : 12, align: 'left', valign: 'top' });
    else body += monogram(initials, { cx: x + 6.5, cy: 7 + 6.5, r: 6.5, accent });
    body += bottomStack({ x, maxW, name, title, companyText, accent, soft });
  } else if (variant === 'split') {
    const divX = 40;
    if (logo) body += image(logo, { x: 7, y: 15, w: 28, h: 24 });
    else body += monogram(initials, { cx: 21, cy: 27, r: 9, accent });
    body += `<rect x="${divX}" y="15" width="0.3" height="24" fill="${accent}"/>`;
    const x = divX + 4.2;
    body += leftBlock({ x, maxW: W - 7 - x, cy: 27, name, title, companyText, accent, soft });
  } else {
    // centered
    const cx = W / 2;
    const maxW = W - 16;
    if (logo) body += image(logo, { x: cx - 21, y: 8, w: 42, h: 15, valign: 'bottom' });
    else body += monogram(initials, { cx, cy: 15.5, r: 7.2, accent });
    const ns = fitSize(name, nameStyle(5.4), maxW, 3);
    const ts = fitSize(title, titleStyle(2.6), maxW, 1.8);
    const cs = fitSize(companyText, companyStyle(2.0), maxW, 1.5);
    body += text(name, { x: cx, y: 33, size: ns, family: DISPLAY_FONT, weight: 800, anchor: 'middle' });
    if (title) body += text(title, { x: cx, y: 38.3, size: ts, fill: soft, anchor: 'middle' });
    body += `<rect x="${n(cx - 4)}" y="41.4" width="8" height="0.5" rx="0.25" fill="${accent}"/>`;
    // letter-spacing adds trailing space after the last glyph; nudge to stay optically centered
    if (company) body += text(companyText, { x: cx + (cs * 0.16) / 2, y: 46.2, size: cs, weight: 600, fill: accent, anchor: 'middle', spacing: cs * 0.16 });
  }

  return `${open(fontCss)}<g id="front">${body}</g></svg>`;
}

/** Contactless symbol (four arcs), drawn in a 10 x 10 box. */
function contactless({ x, y, size, color }) {
  const s = size / 10;
  let d = '';
  for (let i = 0; i < 4; i++) {
    const x0 = 1.4 + i * 2.25;
    const hh = 1.3 + i * 1.12;
    const r = hh * 1.3;
    d += `M${n(x0)} ${n(5 - hh)}A${n(r)} ${n(r)} 0 0 1 ${n(x0)} ${n(5 + hh)}`;
  }
  return `<g transform="translate(${n(x)} ${n(y)}) scale(${n(s)})"><path d="${d}" fill="none" stroke="${color}" stroke-width="0.95" stroke-linecap="round"/></g>`;
}

/**
 * Carduzo mark for a black card, in flat ink (gradients band in print): blue C with a
 * violet card inside, or all white for white-ink-only. Drawn on its 64-unit grid.
 */
function carduzoMark({ x, y, size, whiteOnly = false }) {
  const s = size / 64;
  const c = whiteOnly ? '#FFFFFF' : '#3B7BFF';
  const f = whiteOnly ? '#FFFFFF' : '#A78BFA';
  return `<g transform="translate(${n(x)} ${n(y)}) scale(${n(s)})">`
    + `<path d="M47 11.5L20 21L20 43L47 52.5" fill="none" stroke="${c}" stroke-width="12" stroke-linejoin="round"/>`
    + `<path d="M31.5 28L52.5 21L52.5 43L31.5 36Z" fill="${f}" stroke="${f}" stroke-width="2.4" stroke-linejoin="round"/></g>`;
}

/**
 * Back of the card: QR to the public card, "Tap or scan", Carduzo mark.
 * @param {object} o { url, qrStyle: 'tile'|'modules', accent, fontCss }
 */
export function buildBack(o) {
  const { url, qrStyle = 'tile', fontCss, whiteOnly = false } = o;
  const accent = whiteOnly ? '#FFFFFF' : o.accent || '#FFFFFF';
  const soft = whiteOnly ? '#FFFFFF' : SOFT;
  const muted = whiteOnly ? '#FFFFFF' : MUTED;
  const { d, size } = qrPath(url);
  const tile = { x: 7, y: 9, s: 36, r: 2.6 };
  const margin = 3; // white quiet zone inside the tile (> 2 modules)
  const qrSide = tile.s - 2 * margin;
  const m = qrSide / size;
  const qx = tile.x + margin;
  const qy = tile.y + margin;
  const modules = `<path d="${d}" transform="translate(${n(qx)} ${n(qy)}) scale(${n(m)})"`;
  let qr;
  if (qrStyle === 'modules') {
    // White modules straight on black; the black card is the quiet zone
    qr = `${modules} fill="#FFFFFF"/>`;
  } else {
    // White tile with the modules cut out, so only white ink is printed and the
    // black card forms the dark modules
    qr = '<mask id="qr-cut" maskUnits="userSpaceOnUse" x="0" y="0" width="' + W + '" height="' + H + '">'
      + `<rect x="${tile.x}" y="${tile.y}" width="${tile.s}" height="${tile.s}" rx="${tile.r}" fill="#FFFFFF"/>`
      + `${modules} fill="#000000"/></mask>`
      + `<rect x="${tile.x}" y="${tile.y}" width="${tile.s}" height="${tile.s}" rx="${tile.r}" fill="#FFFFFF" mask="url(#qr-cut)"/>`;
  }

  const x = 50;
  const maxW = W - 6.5 - x;
  const shortUrl = url.replace(/^https?:\/\//, '');
  const us = fitSize(shortUrl, { family: SANS_FONT, weight: 500, sizeMm: 1.9 }, maxW, 1.2);
  let body = qr;
  body += contactless({ x: x - 0.6, y: 12.5, size: 8.5, color: accent });
  body += text('Tap or scan', { x, y: 28, size: 4.3, family: DISPLAY_FONT, weight: 700 });
  body += text('to save my contact', { x, y: 32.4, size: 2.3, fill: soft });
  body += text(shortUrl, { x, y: 37, size: us, fill: muted });
  // The mark's ink starts 13.4/64 into its grid: shift it so it lines up with the text above
  body += carduzoMark({ x: x - 1.34, y: 40.7, size: 6.4, whiteOnly });
  body += text(BRAND.name, { x: x + 4.9, y: 45.2, size: 2.5, family: DISPLAY_FONT, weight: 700 });

  return `${open(fontCss)}<g id="back">${body}</g></svg>`;
}

/** Object URL for an SVG string, revoked when it changes. */
export function useSvgUrl(svg) {
  const [url, setUrl] = useState('');
  useEffect(() => {
    if (!svg) return setUrl('');
    const u = URL.createObjectURL(new Blob([svg], { type: 'image/svg+xml' }));
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [svg]);
  return url;
}

const pct = (mm, of) => `${((mm / of) * 100).toFixed(3)}%`;

/**
 * On-screen mock of the black card with the art on top. Preview only: the
 * exported files never contain this black background.
 */
export function NfcCardMock({ svg, label, showSafe, className = '' }) {
  const url = useSvgUrl(svg);
  return (
    <figure className={`min-w-0 ${className}`}>
      <div
        className="relative w-full shadow-[0_18px_40px_rgba(11,21,48,0.28)]"
        style={{
          aspectRatio: `${W} / ${H}`,
          borderRadius: `${pct(CARD_MM.radius, W)} / ${pct(CARD_MM.radius, H)}`,
          background: 'radial-gradient(120% 140% at 15% 0%, #2a2d33 0%, #111214 45%, #050506 100%)',
        }}
        data-testid={`mock-${label?.toLowerCase()}`}
      >
        {url && <img src={url} alt={`${label} print design`} className="absolute inset-0 w-full h-full" draggable="false" />}
        {showSafe && (
          <div
            className="absolute border border-dashed border-coral pointer-events-none"
            style={{
              left: pct(CARD_MM.safe, W), right: pct(CARD_MM.safe, W),
              top: pct(CARD_MM.safe, H), bottom: pct(CARD_MM.safe, H),
              borderRadius: '2px',
            }}
            aria-hidden="true"
          />
        )}
      </div>
      {label && <figcaption className="mt-3 text-xs font-semibold tracking-wider uppercase text-ink-muted">{label}</figcaption>}
    </figure>
  );
}
